/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import {
  useBeforeUnload,
  useBlocker,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom"
import { toast } from "sonner"

import type { CapabilityMode } from "@/api/admin/delegation"
import type {
  AdminAreaId,
  FairUseViewId,
} from "@/features/admin-dashboard/admin-dashboard-route-state"
import type { ResolvedManagementSectionEntry } from "@/features/admin-dashboard/management-section-registry"

import { AdminSystemHealthTab } from "@/app/pages/admin-system-health-tab"
import { Activity, BarChart3, Coins, PlugZap, Search, Users } from "@/components/icons"
import { ModulePageHeader } from "@/components/layouts/module-page-header"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { EmptyState } from "@/components/ui/empty-state"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Spinner } from "@/components/ui/spinner"
import { useRoutes } from "@/contexts/use-routes"
import { resolveManagementCapabilityModes } from "@/features/admin-dashboard/admin-capability-modes"
import {
  isAdminAreaId,
  isFairUseUsageView,
  isSameAdminEditor,
  resolveAdminArea,
  resolveFairUseView,
  resolveLegacyAdminTarget,
  resolveOperationsView,
  resolveUsageView,
  updateAdminRouteParams,
} from "@/features/admin-dashboard/admin-dashboard-route-state"
import {
  ADMIN_OPERATIONS_VIEW_ENTRIES,
  ADMIN_USAGE_VIEW_ENTRIES,
  getVisibleAdminAreas,
  resolveAreaCapabilityMode,
} from "@/features/admin-dashboard/admin-search-registry"
import {
  AdminUnsavedChangesContext,
  type RegisterAdminDirty,
} from "@/features/admin-dashboard/admin-unsaved-changes-context"
import { AdminDashboardError } from "@/features/admin-dashboard/components/admin-dashboard-error"
import { LoadingCard } from "@/features/admin-dashboard/components/admin-dashboard-loading"
import {
  DashboardSectionCard,
  KPI,
} from "@/features/admin-dashboard/components/admin-dashboard-shared"
import { AdminGlobalSearchDialog } from "@/features/admin-dashboard/components/admin-global-search-dialog"
import { AdminGlobalSearchTrigger } from "@/features/admin-dashboard/components/admin-global-search-trigger"
import { AdminManagementWorkspace } from "@/features/admin-dashboard/components/admin-management-workspace"
import { AdminSubNavigation } from "@/features/admin-dashboard/components/admin-sub-navigation"
import { AdminUnsavedChangesDialog } from "@/features/admin-dashboard/components/admin-unsaved-changes-dialog"
import { AdminUsageNavigation } from "@/features/admin-dashboard/components/admin-usage-navigation"
import { AdminWorkspaceNavigation } from "@/features/admin-dashboard/components/admin-workspace-navigation"
import { AgentRunsChart } from "@/features/admin-dashboard/components/agent-runs-chart"
import { AdminIncidentSummaryCard } from "@/features/admin-dashboard/components/alerts/admin-incident-summary-card"
import { RecentAlertsCard } from "@/features/admin-dashboard/components/alerts/recent-alerts-card"
import { CollapsibleDashboardCard } from "@/features/admin-dashboard/components/collapsible-dashboard-card"
import { CostsByUserTable } from "@/features/admin-dashboard/components/costs-by-user-table"
import { CustomerPreviewToggle } from "@/features/admin-dashboard/components/customer-preview-toggle"
import { DataSourcesTable } from "@/features/admin-dashboard/components/data-sources-table"
import { DocintQuotaCard } from "@/features/admin-dashboard/components/docint/docint-quota-card"
import { FairUseArea } from "@/features/admin-dashboard/components/fair-use/fair-use-area"
import { FeedbackSection } from "@/features/admin-dashboard/components/feedback/feedback-section"
import { HostedAgentInvoiceCard } from "@/features/admin-dashboard/components/hosted-agent-invoice-card"
import { ModuleUsageCard } from "@/features/admin-dashboard/components/module-usage-card"
import { SearchStatsChart } from "@/features/admin-dashboard/components/search-stats-chart"
import { SlideSenseUsageSection } from "@/features/admin-dashboard/components/slide-sense-usage-section"
import { TenderMonitoringSection } from "@/features/admin-dashboard/components/tender-monitoring-section"
import { TokenMetricsByModel } from "@/features/admin-dashboard/components/token-metrics-by-model"
import { TokenUsageChart } from "@/features/admin-dashboard/components/token-usage-chart"
import { TopUsersTable } from "@/features/admin-dashboard/components/top-users-table"
import { TrainingStatusCard } from "@/features/admin-dashboard/components/training-status-card"
import { UsageInsightsSection } from "@/features/admin-dashboard/components/usage-insights-section"
import { useDelegationMatrixQuery } from "@/features/admin-dashboard/hooks/queries/delegation"
import { useAdminData } from "@/features/admin-dashboard/hooks/use-admin-data"
import { useAdminGlobalSearchShortcut } from "@/features/admin-dashboard/hooks/use-admin-global-search-shortcut"
import { useCollapsibleSection } from "@/features/admin-dashboard/hooks/use-collapsible-section"
import { getManagementSections } from "@/features/admin-dashboard/management-section-registry"
import {
  type DateRangePresetId,
  getApproximateDays,
  getDateRangePresets,
  getPresetById,
} from "@/features/admin-dashboard/utils/date-ranges"
import { canSeeHostedAgentInvoice } from "@/features/admin-dashboard/utils/hosted-agent-invoice-utils"
import { isHostedAgentsEnabledInConfig } from "@/hooks/feature-utils"
import { useConfigQuery } from "@/hooks/use-config-query"
import { cn } from "@/lib/utils"
import { useAdminCustomerPreviewStore } from "@/stores/admin-customer-preview-store"

export function AdminDashboardPage() {
  const { t } = useTranslation()
  const { routes } = useRoutes()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { hash, pathname } = useLocation()
  const hasTenderRoute = routes.some((r) => r.id === "tender" && r.enabled)
  const activeArea = resolveAdminArea(searchParams.get("area"))
  const isAnalyticsArea = activeArea === "overview" || activeArea === "usage"
  const usageView = resolveUsageView(searchParams.get("view"))
  const operationsView = resolveOperationsView(searchParams.get("view"), hasTenderRoute)
  const isFairUseView = isFairUseUsageView(usageView)
  const fairUseView = resolveFairUseView(isFairUseView ? usageView.slice("fair-use-".length) : null)
  const selectedSettingId = searchParams.get("setting") ?? undefined
  const [dirtySettingKeys, setDirtySettingKeys] = useState<Set<string>>(() => new Set())
  const hasUnsavedChanges = dirtySettingKeys.size > 0
  const [draftResetVersion, setDraftResetVersion] = useState(0)
  const [pendingPreviewValue, setPendingPreviewValue] = useState<boolean | null>(null)
  const [selectedPresetId, setSelectedPresetId] = useState<DateRangePresetId>("last30Days")
  const [currency, setCurrency] = useState<"SEK" | "USD" | "EUR">("USD")

  // Platform-admin "preview as customer admin" — view-only, filters the
  // Management tab against the live delegation matrix. See
  // features/admin-dashboard/management-section-registry.ts.
  const { data: config, isError: isConfigError, isLoading: isConfigLoading } = useConfigQuery()
  const isPlatformAdmin = config?.user_policy.is_platform_admin ?? false
  // Gated on isPlatformAdmin, not just the persisted flag: the toggle to turn
  // preview off only renders for platform admins, so a stale localStorage
  // value (e.g. after a role downgrade) must never leave a non-platform-admin
  // stuck looking at a preview they have no control to escape.
  const previewRequested = useAdminCustomerPreviewStore((s) => s.enabled)
  const previewActive = previewRequested && isPlatformAdmin
  const previewConfigPending = previewRequested && isConfigLoading && !config
  const previewConfigError = previewRequested && isConfigError && !config
  const setPreviewEnabled = useAdminCustomerPreviewStore((s) => s.setEnabled)
  const {
    data: delegationData,
    isLoading: isDelegationLoading,
    isError: isDelegationError,
  } = useDelegationMatrixQuery(previewActive)
  // Distinct from "genuinely empty" below: while the matrix is still loading
  // (or failed to load), the banner/sidebar must say so instead of rendering
  // the fail-closed empty map as if it were a confirmed "nothing delegated"
  // result — otherwise a platform admin sees "0 active, 0 available, N
  // hidden" before the fetch has even resolved.
  const previewLoading = previewConfigPending || (previewActive && isDelegationLoading)
  // isError alone would stay stuck showing the error banner forever after a
  // later background refetch fails (e.g. one triggered by a delegation
  // control's own save elsewhere on the page), even though the cache still
  // holds a trustworthy matrix — React Query keeps the last good `data`
  // across a failed refetch. Only treat it as an error worth surfacing when
  // there's no cached matrix to fall back on.
  const previewError = previewConfigError || (previewActive && isDelegationError && !delegationData)
  const capabilityModes = useMemo(() => {
    // While the matrix is still loading (or preview is off), an empty map
    // makes every section resolve to "no mapping" — i.e. fully hidden. Fail
    // closed instead of briefly flashing every section as enabled during
    // the fetch.
    if (!delegationData) return {}
    const map: Record<string, CapabilityMode> = {}
    for (const entry of delegationData.registry) {
      // platform_only rows never grant real customer-admin access (the
      // backend's own /api/config resolver, resolve_customer_capabilities,
      // never exposes them either) — skip them so a stale/legacy non-hidden
      // value in the stored matrix can't make the preview show a section as
      // active when a real customer admin would never see it.
      if (entry.tier !== "delegable") continue
      map[entry.key] = entry.mode
    }
    return map
  }, [delegationData])
  const hostedAgentsEnabled = isHostedAgentsEnabledInConfig(config)
  // A platform-admin preview and a real delegated admin both use the same
  // capability-filtered registry; missing data fails closed.
  const previewingCustomer = previewActive || previewConfigPending || previewConfigError
  const sectionModes = useMemo(
    () =>
      resolveManagementCapabilityModes({
        isPlatformAdmin,
        userModes: config?.user_policy.admin_capabilities,
        previewModes: capabilityModes,
        previewingCustomer,
      }),
    [isPlatformAdmin, config?.user_policy.admin_capabilities, capabilityModes, previewingCustomer]
  )
  const capabilityResolvedSections = useMemo(
    () => getManagementSections(hasTenderRoute, sectionModes, hostedAgentsEnabled),
    [hasTenderRoute, sectionModes, hostedAgentsEnabled]
  )

  // Preserve the existing area filter. The current four areas carry no
  // capability; nested Fair use tasks and Settings sections are gated below.
  const visibleAreas = useMemo(
    () =>
      getVisibleAdminAreas(config?.user_policy.admin_capabilities, {
        isPlatformAdmin,
        previewModes: previewingCustomer ? capabilityModes : undefined,
        // Retain the named area during bootstrap; nested capabilities still fail closed.
        keepAreaIds: config ? undefined : [activeArea],
      }),
    [activeArea, capabilityModes, config, isPlatformAdmin, previewingCustomer]
  )
  // What the Fair use area itself must gate on: the previewed customer's
  // delegated mode while previewing, the viewer's own otherwise.
  const fairUseMode = resolveAreaCapabilityMode(
    "fair_use",
    config?.user_policy.admin_capabilities,
    { previewModes: previewingCustomer ? capabilityModes : undefined }
  )
  // The invoice endpoint answers only `hosted_agents` managers (403 for every
  // other customer admin), so the card — and its query — is only mounted for
  // them, and only where hosted agents exist at all.
  const showHostedAgentInvoice = canSeeHostedAgentInvoice({
    hostedAgentsEnabled,
    isPlatformAdmin,
    adminCapabilities: config?.user_policy.admin_capabilities,
    previewModes: previewingCustomer ? capabilityModes : undefined,
  })
  const canSeeFairUse =
    (isPlatformAdmin && !previewingCustomer) ||
    fairUseMode === "manage" ||
    fairUseMode === "available"
  const [lastCleanResolvedSections, setLastCleanResolvedSections] = useState(
    capabilityResolvedSections
  )
  useEffect(() => {
    if (!hasUnsavedChanges) setLastCleanResolvedSections(capabilityResolvedSections)
  }, [capabilityResolvedSections, hasUnsavedChanges])
  // A platform-admin preview is a UI simulation, not an authorization boundary.
  // Keep its last clean registry snapshot while a local draft is dirty so a
  // reconnect/refetch cannot unmount the editor before the user decides.
  const resolvedManagementSections = hasUnsavedChanges
    ? lastCleanResolvedSections
    : capabilityResolvedSections

  const settingsSections = resolvedManagementSections

  const registerDirty = useCallback<RegisterAdminDirty>((key, dirty) => {
    setDirtySettingKeys((current) => {
      const next = new Set(current)
      if (dirty) next.add(key)
      else next.delete(key)
      return next
    })
  }, [])
  const blocker = useBlocker(
    useCallback(
      ({
        currentLocation,
        nextLocation,
      }: {
        currentLocation: { pathname: string; search: string }
        nextLocation: { pathname: string; search: string }
      }) => {
        if (!hasUnsavedChanges) return false
        // react-router blocks EVERY navigation, and inside the admin
        // workspace a tab strip is a navigation: `?view=` is a search param.
        // Switching tabs inside one area is not leaving the editor — the
        // fair-use form lives above the tab switch precisely so it survives —
        // so warning there would offer to discard edits the admin never left.
        // Switching AREA or management SECTION still warns: those really do
        // unmount the editor.
        return !isSameAdminEditor(currentLocation, nextLocation)
      },
      [hasUnsavedChanges]
    )
  )
  useBeforeUnload(
    useCallback(
      (event) => {
        if (!hasUnsavedChanges) return
        event.preventDefault()
        event.returnValue = ""
      },
      [hasUnsavedChanges]
    )
  )

  const navigateTo = (
    next: { area: AdminAreaId; view?: string; setting?: string },
    replace = false
  ) => setSearchParams(updateAdminRouteParams(searchParams, next), { replace })

  const [globalSearchOpen, setGlobalSearchOpen] = useState(false)
  const openGlobalSearch = useCallback(() => setGlobalSearchOpen(true), [])
  // Stay attached while the palette is open so Ctrl/Cmd+K still preventDefaults
  // (onOpen is idempotent).
  useAdminGlobalSearchShortcut(true, openGlobalSearch)

  const handleAreaChange = (area: AdminAreaId) => {
    if (area === activeArea) return
    if (area === "usage") {
      navigateTo({ area, view: usageView })
      return
    }
    if (area === "operations") {
      navigateTo({ area, view: operationsView })
      return
    }
    if (area === "settings") {
      navigateTo({ area, setting: settingsSections[0]?.id })
      return
    }
    navigateTo({ area })
  }

  const handleSelectManagementSection = (section: ResolvedManagementSectionEntry) => {
    navigateTo({ area: "settings", setting: section.id })
  }

  const handlePreviewChange = (next: boolean) => {
    if (!hasUnsavedChanges) {
      setPreviewEnabled(next)
      return
    }
    setPendingPreviewValue(next)
  }

  const handleCancelDiscard = () => {
    if (pendingPreviewValue !== null) {
      setPendingPreviewValue(null)
      return
    }
    if (blocker.state === "blocked") blocker.reset()
  }

  const handleConfirmDiscard = () => {
    setDirtySettingKeys(new Set())
    setDraftResetVersion((version) => version + 1)
    if (pendingPreviewValue !== null) {
      setPreviewEnabled(pendingPreviewValue)
      setPendingPreviewValue(null)
      return
    }
    if (blocker.state === "blocked") blocker.proceed()
  }

  useEffect(() => {
    // Pre-merge fair-use links first — before the area is validated, so a bare
    // `/admin?setting=fair-use` or `/admin#fair-use` is not bounced to the
    // overview on its way. The anchor is dropped with the rewrite so the page
    // does not try to scroll to an element that no longer exists.
    const legacyTarget = resolveLegacyAdminTarget(searchParams, hash)
    if (legacyTarget) {
      void navigate(
        {
          pathname,
          search: `?${updateAdminRouteParams(searchParams, legacyTarget).toString()}`,
          hash: "",
        },
        { replace: true }
      )
      return
    }

    if (!isAdminAreaId(searchParams.get("area"))) {
      setSearchParams(
        updateAdminRouteParams(searchParams, {
          area: "overview",
        }),
        { replace: true }
      )
      return
    }

    if (previewLoading || previewError) return
    if (activeArea === "overview") {
      if (searchParams.has("view") || searchParams.has("setting")) {
        setSearchParams(updateAdminRouteParams(searchParams, { area: "overview" }), {
          replace: true,
        })
      }
      return
    }
    if (activeArea === "usage") {
      if (config && isFairUseView && !canSeeFairUse) {
        setSearchParams(updateAdminRouteParams(searchParams, { area: "usage", view: "activity" }), {
          replace: true,
        })
        return
      }
      if (searchParams.get("view") !== usageView || searchParams.has("setting")) {
        setSearchParams(updateAdminRouteParams(searchParams, { area: "usage", view: usageView }), {
          replace: true,
        })
      }
      return
    }
    if (activeArea === "operations") {
      if (searchParams.get("view") !== operationsView || searchParams.has("setting")) {
        setSearchParams(
          updateAdminRouteParams(searchParams, { area: "operations", view: operationsView }),
          { replace: true }
        )
      }
      return
    }
    const selectedIsAvailable = settingsSections.some((section) => section.id === selectedSettingId)
    if (!selectedIsAvailable && settingsSections[0]) {
      setSearchParams(
        updateAdminRouteParams(searchParams, {
          area: activeArea,
          setting: settingsSections[0].id,
        }),
        { replace: true }
      )
    }
  }, [
    activeArea,
    canSeeFairUse,
    config,
    hash,
    isFairUseView,
    navigate,
    pathname,
    previewError,
    previewLoading,
    operationsView,
    searchParams,
    selectedSettingId,
    setSearchParams,
    settingsSections,
    usageView,
  ])

  const dateRange = { type: "preset" as const, presetId: selectedPresetId }

  // Heavy analytics sections default to collapsed so their slow (~3s Cosmos)
  // queries don't fire on initial load — they fetch only once the user expands
  // them. State is persisted per section so a user's preference sticks.
  const tokenMetricsSection = useCollapsibleSection("admin.dashboard.tokenMetrics", false)
  const agentRunsSection = useCollapsibleSection("admin.dashboard.agentRuns", false)
  const adoptionSection = useCollapsibleSection("admin.dashboard.adoption", false)
  const costsSection = useCollapsibleSection("admin.dashboard.costs", false)
  const usageInsightsSection = useCollapsibleSection("admin.dashboard.usageInsights", false)
  const dataSourcesSection = useCollapsibleSection("admin.dashboard.dataSources", true)
  const tokenUsageSection = useCollapsibleSection("admin.dashboard.tokenUsage", true)
  const searchStatsSection = useCollapsibleSection("admin.dashboard.searchStats", true)

  // Fetch admin data from backend
  const {
    topUsers,
    summary: realSummary,
    summaryComputedAt,
    tokenChartData,
    usageTrendData,
    dataSources: realDataSources,
    agentRunsMonthly: realAgentRunsMonthly,
    searchChartData: realSearchChartData,
    training,
    costsByUser,
    featureAdoption,
    uploadStats,
    featureUsageStats,
    tokenMetrics,
    isFetchingSummary,
    isLoadingTokenMetrics,
    isLoadingCostSummary,
    isLoadingTokenMetricsFetching,
    isLoadingTopUsers,
    isLoadingDataSources,
    isLoadingAgentRuns,
    isLoadingFeatureAdoption,
    isLoadingFeatureUsageStats,
    isLoadingTraining,
    isFetchingTraining,
    refetchTraining,
    isLoadingSearches,
    isLoadingUsage,
    isLoadingCostsByUser,
    error,
    refetch,
  } = useAdminData(dateRange, currency, {
    summaryEnabled:
      activeArea === "overview" ||
      (activeArea === "usage" &&
        (usageView === "activity" ||
          usageView === "training" ||
          (usageView === "costs" && costsSection.isOpen))),
    trainingEnabled: activeArea === "usage" && usageView === "training",
    dataSourcesEnabled:
      activeArea === "usage" && usageView === "activity" && dataSourcesSection.isOpen,
    tokenMetricsEnabled:
      activeArea === "usage" && usageView === "activity" && tokenMetricsSection.isOpen,
    agentRunsEnabled: activeArea === "usage" && usageView === "activity" && agentRunsSection.isOpen,
    adoptionEnabled: activeArea === "usage" && usageView === "adoption" && adoptionSection.isOpen,
    costsByUserEnabled: activeArea === "usage" && usageView === "costs" && costsSection.isOpen,
  })

  // Use real data directly - no fallback to mock data
  const formattedTokenData = useMemo(() => {
    return tokenChartData || []
  }, [tokenChartData])

  const summary = realSummary
  const computedAt = summaryComputedAt ? new Date(summaryComputedAt) : null
  const summaryFreshness =
    computedAt && !Number.isNaN(computedAt.getTime())
      ? t("adminDashboard.workspace.overview.computedAt", "Chat metrics computed {{time}}", {
          time: computedAt.toLocaleString(),
        })
      : t(
          "adminDashboard.workspace.overview.freshnessUnknown",
          "Chat metric calculation time unavailable"
        )
  const displayDataSources = realDataSources
  const displayAgentRunsMonthly = realAgentRunsMonthly
  const displaySearchChartData = realSearchChartData

  // Progressive loading - don't block entire dashboard
  // Show error banner if critical queries fail
  const showErrorBanner = error && !summary && !tokenChartData

  const activePresetLabelKey = getPresetById(selectedPresetId)?.labelKey
  const activePresetLabel = activePresetLabelKey ? t(activePresetLabelKey) : selectedPresetId

  // Only treat the page as "refreshing" when the critical summary is being
  // re-fetched while data is already on screen (manual refresh, range/currency
  // change). On the initial load — and while only the slower secondary queries
  // are still in flight — individual sections show their own skeletons, so the
  // fast KPIs are never dimmed or gated behind the slow analytics queries.
  const isRefreshing = isAnalyticsArea && isFetchingSummary && Boolean(realSummary)
  const activeManagementSections = activeArea === "settings" ? settingsSections : []
  const selectedManagementSection = activeManagementSections.find(
    (section) => section.id === selectedSettingId
  )
  let selectedManagementTitle: string | undefined
  let selectedManagementSubtitle: string | undefined
  if (selectedManagementSection) {
    selectedManagementTitle = selectedManagementSection.titleFallback
      ? t(selectedManagementSection.titleKey, selectedManagementSection.titleFallback)
      : t(selectedManagementSection.titleKey)
    selectedManagementSubtitle = selectedManagementSection.descriptionFallback
      ? t(
          `adminDashboard.workspace.settingDescriptions.${selectedManagementSection.id}`,
          selectedManagementSection.descriptionFallback
        )
      : undefined
  }

  const areaMeta: Record<AdminAreaId, { title: string; subtitle: string }> = {
    overview: {
      title: t("adminDashboard.workspace.overview.title", "Admin overview"),
      subtitle: t(
        "adminDashboard.workspace.overview.subtitle",
        "Key usage and availability at a glance."
      ),
    },
    usage: {
      title: t("adminDashboard.workspace.usageBilling.title", "Usage & billing"),
      subtitle: t(
        "adminDashboard.workspace.usageBilling.subtitle",
        "Explore activity, costs, plans and fair use."
      ),
    },
    operations: {
      title: t("adminDashboard.workspace.operations.title", "Operations"),
      subtitle: t(
        "adminDashboard.workspace.operations.subtitle",
        "Monitor system health, incidents and operational workloads."
      ),
    },
    settings: {
      title: selectedManagementTitle ?? t("adminDashboard.workspace.settings.title", "Settings"),
      subtitle:
        selectedManagementSubtitle ??
        t("adminDashboard.workspace.settings.subtitle", "Choose a setting from the grouped list."),
    },
  }

  const showGlobalAnalyticsActions =
    activeArea === "overview" ||
    (activeArea === "usage" && ["activity", "adoption", "costs", "training"].includes(usageView))
  const showCurrencySelector =
    activeArea === "overview" ||
    (activeArea === "usage" && (usageView === "activity" || usageView === "costs"))
  const analyticsActions = showGlobalAnalyticsActions ? (
    <>
      <Button
        className="min-h-11"
        disabled={isRefreshing}
        type="button"
        variant="outline"
        onClick={async () => {
          try {
            const result = await refetch()
            if (result.status === "skipped") {
              toast.info(t("adminDashboard.page.refreshSkipped", "Refresh skipped"), {
                description: result.message,
              })
            } else {
              toast.success(t("adminDashboard.page.refreshSuccess", "Dashboard refreshed"))
            }
          } catch {
            toast.error(t("adminDashboard.page.refreshError", "Failed to refresh dashboard"))
          }
        }}
      >
        {isRefreshing ? t("common.loading") : t("common.refresh")}
      </Button>
      <Select
        disabled={isRefreshing}
        value={selectedPresetId}
        onValueChange={(value) => setSelectedPresetId(value as DateRangePresetId)}
      >
        <SelectTrigger className="min-h-11 w-full rounded-md sm:w-44">
          <SelectValue placeholder={t("adminDashboard.page.periodSelector.placeholder")} />
        </SelectTrigger>
        <SelectContent>
          {getDateRangePresets().map((preset) => (
            <SelectItem key={preset.id} value={preset.id}>
              {t(preset.labelKey)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {showCurrencySelector ? (
        <Select
          disabled={isRefreshing}
          value={currency}
          onValueChange={(value) => setCurrency(value as "SEK" | "USD" | "EUR")}
        >
          <SelectTrigger className="min-h-11 w-full rounded-md sm:w-28">
            <SelectValue placeholder="Currency" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="SEK">SEK</SelectItem>
            <SelectItem value="USD">USD</SelectItem>
            <SelectItem value="EUR">EUR</SelectItem>
          </SelectContent>
        </Select>
      ) : null}
    </>
  ) : undefined
  let contextualActions = analyticsActions
  if (activeArea === "settings") {
    contextualActions = <CustomerPreviewToggle onRequestChange={handlePreviewChange} />
  } else if (activeArea === "usage" && usageView === "costs") {
    contextualActions = undefined
  }
  const headerActions = (
    <>
      <AdminGlobalSearchTrigger onOpen={openGlobalSearch} />
      {contextualActions}
    </>
  )
  let previewBannerMessage = ""
  if (previewActive && !previewLoading && !previewError) {
    const enabledCount = activeManagementSections.filter(
      (section) => section.renderState === "enabled"
    ).length
    const upsellCount = activeManagementSections.filter(
      (section) => section.renderState === "upsell"
    ).length
    const totalAreaSections = getManagementSections(
      hasTenderRoute,
      undefined,
      hostedAgentsEnabled
    ).length
    const hiddenCount = totalAreaSections - activeManagementSections.length
    previewBannerMessage = t("adminDashboard.management.customerPreview.banner", {
      enabledCount,
      upsellCount,
      hiddenCount,
    })
  }

  return (
    <AdminUnsavedChangesContext.Provider value={registerDirty}>
      <div className="relative min-w-0" data-testid="admin-dashboard-root">
        <AdminGlobalSearchDialog
          areas={visibleAreas}
          fairUseVisible={canSeeFairUse}
          hasTenderRoute={hasTenderRoute}
          open={globalSearchOpen}
          sections={resolvedManagementSections}
          onNavigate={navigateTo}
          onOpenChange={setGlobalSearchOpen}
        />
        {isRefreshing && (
          <div className="pointer-events-none absolute inset-x-0 top-4 z-30 flex justify-center">
            <div className="bg-background/90 text-muted-foreground flex items-center gap-2 rounded-full px-4 py-2 text-sm shadow">
              <Spinner aria-hidden="true" className="size-3.5" />
              <span>
                {t(
                  "adminDashboard.page.loadingIndicatorMessage",
                  "Refreshing data for {{preset}}",
                  {
                    preset: activePresetLabel,
                  }
                )}
              </span>
            </div>
          </div>
        )}
        <div
          data-testid="admin-dashboard-content"
          className={cn(
            "mx-auto grid w-full max-w-[1600px] min-w-0 gap-5 px-4 py-5 transition-opacity sm:px-6 lg:px-8",
            isRefreshing ? "opacity-60" : "opacity-100"
          )}
        >
          {/* Error banner for critical failures */}
          {isAnalyticsArea && showErrorBanner && (
            <AdminDashboardError error={error as Error} onRetry={refetch} />
          )}

          <AdminWorkspaceNavigation
            activeArea={activeArea}
            areas={visibleAreas}
            onAreaChange={handleAreaChange}
          />

          <ModulePageHeader
            actions={headerActions}
            className="px-0 py-2 sm:flex-col sm:gap-3 xl:flex-row xl:items-start"
            subtitle={areaMeta[activeArea].subtitle}
            title={areaMeta[activeArea].title}
          />

          {activeArea === "settings" && previewActive && !previewLoading && !previewError ? (
            <div
              aria-live="polite"
              className="bg-muted/40 text-muted-foreground flex min-h-11 flex-wrap items-center gap-2 rounded-lg border px-4 py-2 text-xs"
              role="status"
            >
              <span>{previewBannerMessage}</span>
            </div>
          ) : null}

          {activeArea === "overview" ? (
            <div className="space-y-5">
              <DashboardSectionCard
                icon={Activity}
                id="overview"
                title={t("adminDashboard.sections.overview")}
              >
                {summary ? (
                  <p className="text-muted-foreground mb-3 text-xs">{summaryFreshness}</p>
                ) : null}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  {summary ? (
                    <>
                      <KPI
                        icon={Activity}
                        isLoading={isFetchingSummary}
                        label={t("adminDashboard.kpi.totalTokens")}
                        sub={t("adminDashboard.kpi.totalTokensSub")}
                        value={summary.tokens}
                      />
                      <KPI
                        icon={Coins}
                        isLoading={isLoadingCostSummary}
                        label={t("adminDashboard.kpi.cost")}
                        sub={t("adminDashboard.kpi.costSub")}
                        value={summary.cost}
                      />
                      <KPI
                        icon={Search}
                        isLoading={isFetchingSummary}
                        label={t("adminDashboard.kpi.aiSearches")}
                        sub={`${summary.searchesChange} ${t("adminDashboard.kpi.aiSearchesSub")}`}
                        value={summary.searches}
                      />
                      <KPI
                        icon={Users}
                        isLoading={isFetchingSummary}
                        label={t("adminDashboard.kpi.activeUsers")}
                        value={summary.users}
                        sub={t("adminDashboard.kpi.activeUsersSub", {
                          days: getApproximateDays(dateRange),
                        })}
                      />
                    </>
                  ) : (
                    Array.from({ length: 4 }).map((_, index) => (
                      <Card key={index} className="rounded-lg shadow-sm">
                        <CardContent className="p-5">
                          <Skeleton className="h-16 w-full" />
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </DashboardSectionCard>
              <AdminIncidentSummaryCard
                onOpen={() => navigateTo({ area: "operations", view: "alerts" })}
              />
            </div>
          ) : null}

          {activeArea === "usage" ? (
            <div className="grid min-w-0 gap-5 xl:grid-cols-[15rem_minmax(0,1fr)]">
              <AdminUsageNavigation
                value={usageView}
                views={ADMIN_USAGE_VIEW_ENTRIES.filter(
                  (view) => canSeeFairUse || !view.id.startsWith("fair-use-")
                )}
                onValueChange={(view) => navigateTo({ area: "usage", view })}
              />
              <div className="min-w-0 space-y-5">
                {usageView === "activity" ? (
                  <>
                    <DashboardSectionCard
                      icon={BarChart3}
                      id="usage"
                      title={t("adminDashboard.sections.usage")}
                    >
                      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                        <CollapsibleDashboardCard
                          icon={Activity}
                          open={tokenUsageSection.isOpen}
                          title={t("adminDashboard.charts.tokenUsage.title")}
                          onOpenChange={tokenUsageSection.setIsOpen}
                        >
                          <TokenUsageChart
                            data={formattedTokenData}
                            isLoading={isLoadingUsage || isFetchingSummary}
                          />
                        </CollapsibleDashboardCard>

                        <CollapsibleDashboardCard
                          icon={Search}
                          open={searchStatsSection.isOpen}
                          title={t("adminDashboard.charts.searchStats.title")}
                          onOpenChange={searchStatsSection.setIsOpen}
                        >
                          <SearchStatsChart
                            data={displaySearchChartData}
                            isLoading={isLoadingSearches || isFetchingSummary}
                          />
                        </CollapsibleDashboardCard>
                      </div>

                      <CollapsibleDashboardCard
                        icon={Activity}
                        open={tokenMetricsSection.isOpen}
                        title={t("adminDashboard.charts.tokenMetrics.title")}
                        headerActions={
                          isLoadingTokenMetricsFetching ? (
                            <Spinner className="size-4 opacity-50" />
                          ) : undefined
                        }
                        onOpenChange={tokenMetricsSection.setIsOpen}
                      >
                        <div>
                          {isLoadingTokenMetrics && (
                            <div className="space-y-4">
                              {/* Summary skeleton */}
                              <div className="grid grid-cols-2 gap-4 rounded-lg border p-3 md:grid-cols-4">
                                {Array.from({ length: 4 }).map((_, i) => (
                                  <div key={i} className="space-y-2">
                                    <Skeleton className="h-3 w-20" />
                                    <Skeleton className="h-6 w-16" />
                                  </div>
                                ))}
                              </div>
                              {/* Per model skeleton */}
                              <div className="space-y-3">
                                {Array.from({ length: 2 }).map((_, i) => (
                                  <div key={i} className="rounded-lg border p-3">
                                    <div className="mb-2 flex items-center justify-between">
                                      <Skeleton className="h-5 w-32" />
                                      <Skeleton className="h-4 w-24" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                                      {Array.from({ length: 4 }).map((_, j) => (
                                        <div key={j} className="space-y-1">
                                          <Skeleton className="h-3 w-16" />
                                          <Skeleton className="h-4 w-20" />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {!isLoadingTokenMetrics && tokenMetrics && (
                            <TokenMetricsByModel currency={currency} metrics={tokenMetrics} />
                          )}
                          {!isLoadingTokenMetrics && !tokenMetrics && (
                            <EmptyState
                              description={t("adminDashboard.charts.tokenMetrics.emptyDescription")}
                              icon={Activity}
                              title={t("adminDashboard.charts.tokenMetrics.emptyTitle")}
                            />
                          )}
                        </div>
                      </CollapsibleDashboardCard>

                      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                        <CollapsibleDashboardCard
                          icon={PlugZap}
                          open={dataSourcesSection.isOpen}
                          title={t("adminDashboard.tables.dataSources.title")}
                          onOpenChange={dataSourcesSection.setIsOpen}
                        >
                          {isLoadingDataSources ? (
                            <LoadingCard
                              icon={PlugZap}
                              rows={4}
                              title={t("adminDashboard.tables.dataSources.title")}
                              variant="table-row"
                            />
                          ) : (
                            <DataSourcesTable sources={displayDataSources} />
                          )}
                        </CollapsibleDashboardCard>

                        <CollapsibleDashboardCard
                          icon={BarChart3}
                          open={agentRunsSection.isOpen}
                          title={t("adminDashboard.tables.agentRuns.title")}
                          onOpenChange={agentRunsSection.setIsOpen}
                        >
                          {isLoadingAgentRuns ? (
                            <Skeleton className="h-[260px] w-full rounded-xl" />
                          ) : (
                            <AgentRunsChart data={displayAgentRunsMonthly} />
                          )}
                        </CollapsibleDashboardCard>
                      </div>
                    </DashboardSectionCard>

                    <div className="scroll-mt-24" id="usage-insights">
                      <UsageInsightsSection
                        dateRange={dateRange}
                        open={usageInsightsSection.isOpen}
                        onOpenChange={usageInsightsSection.setIsOpen}
                      />
                    </div>
                  </>
                ) : null}

                {usageView === "adoption" ? (
                  <>
                    <div className="scroll-mt-24" id="adoption">
                      <ModuleUsageCard
                        adoption={featureAdoption}
                        featureStats={featureUsageStats}
                        isLoading={isLoadingFeatureAdoption || isLoadingFeatureUsageStats}
                        open={adoptionSection.isOpen}
                        uploadStats={uploadStats}
                        onOpenChange={adoptionSection.setIsOpen}
                      />
                    </div>

                    <div className="scroll-mt-24" id="slide-sense-usage">
                      <SlideSenseUsageSection />
                    </div>
                  </>
                ) : null}

                {usageView === "costs" ? (
                  <>
                    <div className="scroll-mt-24 space-y-3" id="costs">
                      <div className="flex flex-wrap items-end justify-between gap-3">
                        <p className="text-muted-foreground max-w-prose text-sm">
                          {t(
                            "adminDashboard.workspace.usageBilling.chatCostScope",
                            "Chat cost by user · selected range and currency. Monthly quotas below have their own period and currency."
                          )}
                        </p>
                        <div className="flex flex-wrap items-center gap-2">{analyticsActions}</div>
                      </div>
                      <CostsByUserTable
                        data={costsByUser}
                        isLoading={isLoadingCostsByUser}
                        isTrendLoading={isLoadingUsage || isFetchingSummary}
                        open={costsSection.isOpen}
                        trendCurrency={currency}
                        trendData={usageTrendData}
                        onOpenChange={costsSection.setIsOpen}
                      />
                    </div>

                    <div className="scroll-mt-24" id="docint-quota">
                      <DocintQuotaCard />
                    </div>

                    {showHostedAgentInvoice ? (
                      <div className="scroll-mt-24" id="hosted-agent-invoice">
                        <HostedAgentInvoiceCard />
                      </div>
                    ) : null}
                  </>
                ) : null}

                {usageView === "training" ? (
                  <>
                    <div className="scroll-mt-24" id="training">
                      <TrainingStatusCard
                        data={training}
                        isFetching={isFetchingTraining}
                        isLoading={isLoadingTraining}
                        onRefresh={refetchTraining}
                      />
                    </div>

                    <div className="scroll-mt-24" id="top-users">
                      {isLoadingTopUsers && (
                        <Card className="rounded-lg shadow-sm">
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                              <Users className="size-5" />
                              {t("adminDashboard.tables.topUsers.title")}
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-3">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <div key={i} className="flex items-center justify-between">
                                  <Skeleton className="h-4 w-32" />
                                  <div className="flex gap-4">
                                    <Skeleton className="h-4 w-12" />
                                    <Skeleton className="h-4 w-16" />
                                    <Skeleton className="h-4 w-12" />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}
                      {!isLoadingTopUsers && topUsers && <TopUsersTable users={topUsers} />}
                      {!isLoadingTopUsers && !topUsers && (
                        <Card className="rounded-lg shadow-sm">
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                              <Users className="size-5" />
                              {t("adminDashboard.tables.topUsers.title")}
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <EmptyState
                              description={t("adminDashboard.tables.topUsers.emptyDescription")}
                              icon={Users}
                              title={t("adminDashboard.tables.topUsers.emptyTitle")}
                            />
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </>
                ) : null}

                {usageView === "feedback" ? (
                  <div className="scroll-mt-24">
                    <FeedbackSection />
                  </div>
                ) : null}
                {isFairUseView ? (
                  <FairUseArea
                    key={`fair-use-${draftResetVersion}`}
                    isPlatformAdmin={!previewingCustomer && isPlatformAdmin}
                    mode={fairUseMode}
                    showNavigation={false}
                    view={fairUseView}
                    onViewChange={(view: FairUseViewId) =>
                      navigateTo({ area: "usage", view: `fair-use-${view}` })
                    }
                  />
                ) : null}
              </div>
            </div>
          ) : null}

          {activeArea === "operations" ? (
            <div className="min-w-0 space-y-5">
              <AdminSubNavigation
                value={operationsView}
                ariaLabel={t(
                  "adminDashboard.workspaceNavigation.operationsAriaLabel",
                  "Operations views"
                )}
                mobileLabel={t(
                  "adminDashboard.workspaceNavigation.operationsMobileLabel",
                  "Operations view"
                )}
                options={ADMIN_OPERATIONS_VIEW_ENTRIES.filter(
                  (view) => !view.requiresTenderRoute || hasTenderRoute
                ).map((view) => ({
                  id: view.id,
                  label: t(view.labelKey, view.labelFallback),
                }))}
                onValueChange={(view) => navigateTo({ area: "operations", view })}
              />
              {operationsView === "health" ? <AdminSystemHealthTab /> : null}
              {operationsView === "alerts" ? <RecentAlertsCard /> : null}
              {operationsView === "tenders" && hasTenderRoute ? <TenderMonitoringSection /> : null}
            </div>
          ) : null}

          {activeArea === "settings" ? (
            <AdminManagementWorkspace
              key={`${activeArea}-${draftResetVersion}`}
              previewError={previewError}
              previewLoading={previewLoading}
              sections={resolvedManagementSections}
              selectedSettingId={selectedSettingId}
              taskArea="all"
              onSelectSetting={handleSelectManagementSection}
            />
          ) : null}
        </div>
        <AdminUnsavedChangesDialog
          open={blocker.state === "blocked" || pendingPreviewValue !== null}
          onCancel={handleCancelDiscard}
          onDiscard={handleConfirmDiscard}
        />
      </div>
    </AdminUnsavedChangesContext.Provider>
  )
}
